import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);

    const showNotification = useCallback((message, type = 'info', duration = 4000) => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, message, type, duration }]);

        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, duration);
    }, []);

    const dismiss = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const icons = {
        success: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="rgba(16,185,129,0.15)" />
                <path d="M6 10.5L8.5 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        error: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="rgba(239,68,68,0.15)" />
                <path d="M7 7L13 13M13 7L7 13" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        info: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="rgba(59,130,246,0.15)" />
                <path d="M10 9V14M10 6.5V7" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
            </svg>
        )
    };

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            {children}
            <div className="toast-container" role="status" aria-live="polite">
                {notifications.map(n => (
                    <div key={n.id} className={`toast toast-${n.type}`}>
                        <div className="toast-icon-wrap">
                            {icons[n.type]}
                        </div>
                        <div className="toast-body">
                            <span className="toast-message">{n.message}</span>
                        </div>
                        <button
                            className="toast-dismiss"
                            onClick={() => dismiss(n.id)}
                            aria-label="Dismiss"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                        <div
                            className="toast-timer"
                            style={{ animationDuration: `${n.duration}ms` }}
                        />
                    </div>
                ))}
            </div>
        </NotificationContext.Provider>
    );
}

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
